import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, BookOpen, Eye, Ear, Sparkles, Users, Compass } from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";

// Journey progression for PVC-001: SEE → HEAR → HONOR → STAY → CARRY
const JOURNEY = [
  { code: "SEE", label: "See the Person", icon: Eye },
  { code: "HEAR", label: "Hear the Voice", icon: Ear },
  { code: "HONOR", label: "Honor What Matters", icon: Sparkles },
  { code: "STAY", label: "Stay Present", icon: Users },
  { code: "CARRY", label: "Carry the Voice Forward", icon: Compass },
];

export default function PortalLandingPvc001() {
  usePageMeta({
    title: "PVC-001 — The Patient's Voice in Care | CCCHAPS Academy",
    description: "In-development interdisciplinary course. Bringing the Patient's Voice Into Hospice and Healthcare Practice.",
    noindex: true, // In-development · not yet enrolling. Do not compete in search.
  });

  return (
    <div className="bg-[#FDFCF6] min-h-screen" data-testid="portal-pvc-landing">
      {/* Portal Nav */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/academy/portal" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#4A6FA5] text-white flex items-center justify-center">
              <GraduationCap className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-heading text-slate-900 text-sm font-semibold leading-tight">CCCHAPS</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 leading-tight">Academy</div>
            </div>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <Link
              to="/academy/portal/pvc-001/learn"
              data-testid="pvc-nav-start"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]"
            >
              Start Course <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-3">
            <span
              data-testid="pvc-status-badge"
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-800 mb-4"
            >
              In Development · Not Yet Enrolling
            </span>
            <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-3">
              CCCHAPS Academy · PVC-001
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]" data-testid="pvc-title">
              The Patient&rsquo;s Voice in Care
            </h1>
            <p className="mt-3 font-heading text-2xl sm:text-3xl text-slate-700 leading-tight" data-testid="pvc-subtitle">
              Bringing the Patient&rsquo;s Voice Into Hospice and Healthcare Practice
            </p>
            <p className="mt-6 text-lg text-slate-700 leading-relaxed max-w-xl">
              This five-session course invites hospice and healthcare professionals to look beyond diagnosis, tasks, and clinical routines to the person receiving care.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-xl">
              Based on <em>From Diagnosis to Goodbye: Restoring the Patient&rsquo;s Voice Through Every Stage of the Journey</em> by Chaplain Derrick Hall. Through reflection, patient and family perspectives, practical scenarios, and interdisciplinary application, participants explore how to keep the person seen, heard, and valued from diagnosis through end-of-life care and the transition into grief.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/academy/portal/pvc-001/learn"
                data-testid="pvc-cta-start"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] shadow-sm"
              >
                Start Course <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/academy/portal/pvc-001/learn"
                data-testid="pvc-cta-continue"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-white"
              >
                Continue Course
              </Link>
            </div>
            <p className="mt-4 text-xs text-slate-500 uppercase tracking-[0.14em] font-semibold">
              5 Sessions · Self-Paced · Interdisciplinary Professional Education
            </p>
          </div>

          {/* Journey card */}
          <aside className="lg:col-span-2">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6" data-testid="pvc-journey-card">
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Course Journey</p>
              <ol className="space-y-3" data-testid="pvc-journey-list">
                {JOURNEY.map((step, i) => {
                  const Icon = step.icon;
                  return (
                    <li key={step.code} className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#4A6FA5]/10 text-[#4A6FA5] shrink-0">
                        <Icon className="w-4 h-4" strokeWidth={1.75} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#4A6FA5] font-semibold">
                          Session {i + 1} · {step.code}
                        </p>
                        <p className="text-sm text-slate-800 font-medium leading-snug">{step.label}</p>
                      </div>
                    </li>
                  );
                })}
              </ol>
              <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-600">
                <BookOpen className="w-4 h-4 text-slate-400" strokeWidth={1.75} />
                <span>SEE → HEAR → HONOR → STAY → CARRY</span>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Who it's for + distinction */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="pvc-audience-card">
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">Designed for</p>
            <p className="text-sm text-slate-700 leading-relaxed">
              Nurses, social workers, hospice aides, physicians and providers, chaplains, care coordinators, volunteers, bereavement staff, and healthcare leaders who want to keep the person&rsquo;s voice present throughout serious illness and end-of-life care.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="pvc-distinction-card">
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">A note on distinction</p>
            <p className="text-sm text-slate-700 leading-relaxed">
              PVC-001 is <em>not</em> chaplain training. It is an interdisciplinary patient-centered care course and is intentionally distinct from Track Three Hospice Chaplaincy (HOSP-001), which remains the deeper specialty pathway for spiritual-care providers.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
